// Catalogue gallery — displays the master spec sheets from the 2024 brochure
// in a clean lightbox-style grid. Source: master.pdf pages 1-31.
import React, { useState } from "react";
import { PageHero, Section } from "@/components/Section";
import { X, Download, ChevronLeft, ChevronRight, FileText } from "lucide-react";

// 29 product spec pages (pages 30-31 are back-cover; we skip them in the grid).
// Page titles mirror the master.pdf catalogue order.
const SPEC_PAGES = [
  { i: 1, title: "Double Open Ended Spanners" },
  { i: 2, title: "Bihexagon Ring Spanners" },
  { i: 3, title: "Combination Wrench" },
  { i: 4, title: "Gear Wrench" },
  { i: 5, title: "Spark Plug Open End Spanners" },
  { i: 6, title: "Universal Joint Sockets" },
  { i: 7, title: "Crowfoot Spanners" },
  { i: 8, title: "Tubular Ring Spanners" },
  { i: 9, title: "Box End Spanners" },
  { i: 10, title: "T-Type Socket Spanners" },
  { i: 11, title: "Wheel & Rim Wrenches" },
  { i: 12, title: "Flare Nut Wrenches" },
  { i: 13, title: "Blind Hole Press" },
  { i: 14, title: "Adjustable Wrenches" },
  { i: 15, title: "Pipe Wrenches" },
  { i: 16, title: "Hammers — Ball & Cross Pein, Sledge" },
  { i: 17, title: "Speciality Hammers, Mallets & Axes" },
  { i: 18, title: "Chisels, Marking & Punches" },
  { i: 19, title: "Grease Guns" },
  { i: 20, title: "Bench Vices & C-Clamps" },
  { i: 21, title: "Oil Filter Wrenches" },
  { i: 22, title: "Speciality Pliers" },
  { i: 23, title: "Cutters — Bolt, Cable, Tin, PVC, Wire" },
  { i: 24, title: "Screwdrivers — Electrician & Engineer" },
  { i: 25, title: "Speciality Screwdrivers & Testers" },
  { i: 26, title: "Workshop Tools — Planes, Dies, Jacks, Riveters" },
  { i: 27, title: "Tool Storage — Boxes, Trolleys, Kits" },
  { i: 28, title: "Measuring & Speciality Tools" },
  { i: 29, title: "Non-Sparking Tools — Aluminum Bronze" },
];

const pad = (n) => String(n).padStart(2, "0");
const specSrc = (i) => `/products/specs/m-${pad(i)}.jpg`;

const Catalogue = () => {
  const [openIdx, setOpenIdx] = useState(null);

  const open = openIdx !== null ? SPEC_PAGES[openIdx] : null;
  const prev = () => setOpenIdx((i) => (i - 1 + SPEC_PAGES.length) % SPEC_PAGES.length);
  const next = () => setOpenIdx((i) => (i + 1) % SPEC_PAGES.length);

  return (
    <>
      <PageHero
        eyebrow="Master Catalogue 2024"
        title="Every product, every spec"
        intro="Browse the full Jhalani Hand Tools catalogue — sizes, materials, features and applications for each product family."
        breadcrumbs={<>Home / <span className="text-white">Catalogue</span></>}
      />
      <Section>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="eyebrow">{SPEC_PAGES.length} product families</div>
          <a
            href="https://customer-assets.emergentagent.com/job_owner-finder-8/artifacts/bv0m9ts9_master.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="jt-btn jt-btn-outline"
            data-testid="download-catalogue"
          >
            <Download className="w-4 h-4" /> Download Full PDF
          </a>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalogue-grid">
          {SPEC_PAGES.map((p, idx) => (
            <button
              key={p.i}
              onClick={() => setOpenIdx(idx)}
              className="jt-card group block text-left"
              data-testid={`catalogue-item-${p.i}`}
            >
              <div className="aspect-[3/4] bg-white overflow-hidden">
                <img
                  src={specSrc(p.i)}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
              <div className="p-4 border-t border-[var(--jt-border)] group-hover:border-[var(--jt-primary)] transition-colors">
                <div className="font-mono text-[0.65rem] tracking-widest uppercase text-[var(--jt-text-muted)]">
                  Page {pad(p.i)} · 2024 Catalogue
                </div>
                <h3 className="mt-1 font-display font-bold text-base leading-tight">{p.title}</h3>
              </div>
            </button>
          ))}
        </div>
      </Section>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex flex-col"
          onClick={(e) => { if (e.target === e.currentTarget) setOpenIdx(null); }}
          data-testid="catalogue-lightbox"
        >
          <div className="flex items-center justify-between p-4 text-white">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-[var(--jt-primary)]" />
              <span className="font-mono text-xs tracking-widest uppercase">Page {pad(open.i)} of 29</span>
              <span className="text-white/40">·</span>
              <span className="font-display font-bold">{open.title}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={prev} aria-label="Previous" className="p-2 hover:text-[var(--jt-primary)]" data-testid="lightbox-prev"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={next} aria-label="Next" className="p-2 hover:text-[var(--jt-primary)]" data-testid="lightbox-next"><ChevronRight className="w-5 h-5" /></button>
              <button onClick={() => setOpenIdx(null)} aria-label="Close" className="p-2 hover:text-[var(--jt-primary)]" data-testid="lightbox-close"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
            <img src={specSrc(open.i)} alt={open.title} className="max-w-full max-h-full object-contain shadow-2xl" />
          </div>
        </div>
      )}
    </>
  );
};

export default Catalogue;
